/**
 * Pure pedigree math over a known set of animals: ancestry walks, cycle checks, completeness,
 * relationship warnings and Wright inbreeding coefficients (tabular coancestry). Unknown parents are
 * unknown: a missing generation lowers completeness and is never reported as "unrelated".
 */

export interface PedNode { id: string; sire_id: string | null; dam_id: string | null }
export type PedMap = Record<string, PedNode | undefined>;

/** Ancestors of `id` up to `depth` generations: ancestor id -> generations back (shortest path). */
export function ancestors(map: PedMap, id: string, depth: number): Map<string, number> {
  const out = new Map<string, number>();
  let frontier = [id];
  for (let gen = 1; gen <= depth && frontier.length; gen++) {
    const next: string[] = [];
    for (const x of frontier) {
      const n = map[x];
      if (!n) continue;
      for (const p of [n.sire_id, n.dam_id]) {
        if (!p) continue;
        if (!out.has(p)) out.set(p, gen);
        next.push(p);
      }
    }
    frontier = next;
  }
  return out;
}

/** True when making `parentId` a parent of `childId` would create a loop (the parent descends from the child). */
export function createsCycle(map: PedMap, childId: string, parentId: string, maxDepth = 40): boolean {
  if (childId === parentId) return true;
  const seen = new Set<string>();
  let frontier = [parentId];
  for (let i = 0; i < maxDepth && frontier.length; i++) {
    const next: string[] = [];
    for (const x of frontier) {
      if (seen.has(x)) continue;
      seen.add(x);
      const n = map[x];
      if (!n) continue;
      for (const p of [n.sire_id, n.dam_id]) {
        if (!p) continue;
        if (p === childId) return true;
        next.push(p);
      }
    }
    frontier = next;
  }
  return false;
}

/** How much of an animal's pedigree is known within `depth` generations (2 + 4 + 8 ...). */
export function completeness(map: PedMap, id: string, depth: number): { known: number; possible: number; byGeneration: Array<{ known: number; possible: number }> } {
  const byGeneration: Array<{ known: number; possible: number }> = [];
  let slots: Array<string | null> = [id];
  let known = 0;
  let possible = 0;
  for (let gen = 1; gen <= depth; gen++) {
    const next: Array<string | null> = [];
    for (const x of slots) {
      const n = x ? map[x] : undefined;
      next.push(n?.sire_id ?? null, n?.dam_id ?? null);
    }
    const k = next.filter(Boolean).length;
    byGeneration.push({ known: k, possible: next.length });
    known += k;
    possible += next.length;
    slots = next;
  }
  return { known, possible, byGeneration };
}

/**
 * Coancestry (kinship) of two animals within the known pedigree, by the tabular method. The
 * offspring of x and y would have inbreeding F = coancestry(x, y). Unknown parents count as
 * unrelated founders, so the result is "inbreeding found in the known pedigree", a lower bound.
 */
export function makeCoancestry(map: PedMap): { coancestry(x: string | null, y: string | null): number; inbreeding(id: string): number } {
  const order = new Map<string, number>();
  const orderOf = (id: string | null, guard = 0): number => {
    if (!id || !map[id] || guard > 60) return 0;
    const cached = order.get(id);
    if (cached !== undefined) return cached;
    const n = map[id]!;
    const o = 1 + Math.max(orderOf(n.sire_id, guard + 1), orderOf(n.dam_id, guard + 1));
    order.set(id, o);
    return o;
  };
  const memo = new Map<string, number>();
  const coancestry = (x: string | null, y: string | null, guard = 0): number => {
    if (!x || !y || guard > 120) return 0;
    const key = x < y ? `${x}|${y}` : `${y}|${x}`;
    const cached = memo.get(key);
    if (cached !== undefined) return cached;
    let value: number;
    if (x === y) {
      const n = map[x];
      value = 0.5 * (1 + (n ? coancestry(n.sire_id, n.dam_id, guard + 1) : 0));
    } else {
      // Expand the younger animal; an ancestor always has a lower order than its descendants.
      const [young, old] = orderOf(x) >= orderOf(y) ? [x, y] : [y, x];
      const n = map[young];
      value = n ? 0.5 * (coancestry(n.sire_id, old, guard + 1) + coancestry(n.dam_id, old, guard + 1)) : 0;
    }
    memo.set(key, value);
    return value;
  };
  return {
    coancestry: (x, y) => coancestry(x, y),
    inbreeding: (id) => { const n = map[id]; return n ? coancestry(n.sire_id, n.dam_id) : 0; },
  };
}

export type RelationKind = "same" | "parent" | "offspring" | "ancestor" | "descendant" | "shared_ancestor";

export interface Relation {
  kind: RelationKind;
  /** The shared or connecting animal. */
  ancestor_id: string;
  /** Generations from the first animal (sire) and from the second (female) to the ancestor. */
  sire_gens: number;
  female_gens: number;
}

/** Plain relationships between a prospective sire and a female, closest first. */
export function relations(map: PedMap, sireId: string, femaleId: string, depth: number): Relation[] {
  if (sireId === femaleId) return [{ kind: "same", ancestor_id: sireId, sire_gens: 0, female_gens: 0 }];
  const a = ancestors(map, sireId, depth);
  const b = ancestors(map, femaleId, depth);
  const out: Relation[] = [];
  const femaleUp = b.get(sireId);
  if (femaleUp !== undefined) out.push({ kind: femaleUp === 1 ? "parent" : "ancestor", ancestor_id: sireId, sire_gens: 0, female_gens: femaleUp });
  const sireUp = a.get(femaleId);
  if (sireUp !== undefined) out.push({ kind: sireUp === 1 ? "offspring" : "descendant", ancestor_id: femaleId, sire_gens: sireUp, female_gens: 0 });
  const shared: Relation[] = [];
  for (const [anc, ga] of a) {
    const gb = b.get(anc);
    if (gb === undefined) continue;
    shared.push({ kind: "shared_ancestor", ancestor_id: anc, sire_gens: ga, female_gens: gb });
  }
  // Keep only the closest shared ancestors: drop one whose own descendant is also shared on both sides.
  const closest = shared.filter((s) => !shared.some((o) => o !== s && o.sire_gens < s.sire_gens && o.female_gens < s.female_gens && ancestors(map, o.ancestor_id, depth).has(s.ancestor_id)));
  closest.sort((x, y) => (x.sire_gens + x.female_gens) - (y.sire_gens + y.female_gens));
  return [...out, ...closest];
}

export type Risk = "none_found" | "low" | "watch" | "high";

/** Offspring inbreeding bands: 6.25% is a first-cousin mating, 12.5% a half-sibling or grandparent mating. */
export function riskOf(f: number): Risk {
  if (f >= 0.125) return "high";
  if (f >= 0.0625) return "watch";
  if (f > 0) return "low";
  return "none_found";
}

/** "3.1%" style percent with one decimal. */
export function percent(f: number): string {
  const v = Math.round(f * 1000) / 10;
  return `${v}%`;
}

export type ConditionCall = "free" | "carrier" | "affected" | "inconclusive";

/**
 * Recessive conditions for a pairing. A condition the sire carries is a risk when the female carries
 * it too, and unknown when she wasn't tested for it. Untested never counts as free.
 */
export function recessiveFindings(sire: Array<{ condition: string; result: ConditionCall }>, female: Array<{ condition: string; result: ConditionCall }>): Array<{ condition: string; status: "risk" | "unknown" }> {
  const out: Array<{ condition: string; status: "risk" | "unknown" }> = [];
  const key = (c: string) => c.trim().toUpperCase();
  const seen = new Set<string>();
  const check = (carrier: typeof sire, other: typeof sire) => {
    for (const c of carrier) {
      if (c.result !== "carrier" && c.result !== "affected") continue;
      const k = key(c.condition);
      if (seen.has(k)) continue;
      seen.add(k);
      const o = other.find((x) => key(x.condition) === k);
      if (o && (o.result === "carrier" || o.result === "affected")) out.push({ condition: k, status: "risk" });
      else if (!o || o.result === "inconclusive") out.push({ condition: k, status: "unknown" });
    }
  };
  check(sire, female);
  check(female, sire);
  return out;
}
